/**
 * Authentication Service
 * Canonical implementation of authentication endpoints
 */

import { buildServiceUrl } from "../../config/services-config";
import type { HttpClient } from "../client";
import type {
	LoginRequest,
	LoginResponse,
	LogoutRequest,
	RefreshTokenRequest,
	RefreshTokenResponse,
	TokenVerificationResponse,
} from "../types";

export class AuthService {
	private httpClient: HttpClient;

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	// ==================== Authentication Methods ====================

	async login(request: LoginRequest): Promise<LoginResponse> {
		const url = buildServiceUrl("auth", "/api/auth/login");
		return await this.httpClient.post<LoginResponse>(url, request);
	}

	async refreshToken(
		request: RefreshTokenRequest,
	): Promise<RefreshTokenResponse> {
		const url = buildServiceUrl("auth", "/api/auth/refresh");
		return await this.httpClient.post<RefreshTokenResponse>(url, request);
	}

	async logout(request: LogoutRequest = {}): Promise<void> {
		const url = buildServiceUrl("auth", "/api/auth/logout");
		await this.httpClient.post<void>(url, request);
	}

	async verifyToken(): Promise<TokenVerificationResponse> {
		const url = buildServiceUrl("auth", "/api/auth/me");
		const response = await this.httpClient.get<{ user: any; tenant: any }>(url);
		return {
			valid: true,
			user: response.user,
			tenant: response.tenant,
		};
	}

	async changePassword(
		currentPassword: string,
		newPassword: string,
	): Promise<void> {
		const url = buildServiceUrl("auth", "/api/auth/change-password");
		await this.httpClient.post<void>(url, {
			currentPassword,
			newPassword,
		});
	}

	async requestPasswordReset(email: string): Promise<void> {
		const url = buildServiceUrl("auth", "/api/auth/forgot-password");
		await this.httpClient.post<void>(url, { email });
	}

	async resetPassword(token: string, newPassword: string): Promise<void> {
		const url = buildServiceUrl("auth", "/api/auth/reset-password");
		await this.httpClient.post<void>(url, {
			token,
			newPassword,
		});
	}
}
